import React, { useEffect, useRef, useState } from 'react';
import { BubbleMenu, FloatingMenu } from '@tiptap/react';
import useCollabEditor from 'hooks/useCollabEditor';
import { BiCopy, BiPencil, BiUnlink } from 'react-icons/bi';
import { LinkModal } from '../Modal/LinkModal/LinkModal';
import CommentsSidebar from './Components/CommentsSidebar';
import ContentSettings from './Components/ContentSettings';
import DocumentHealth from './Components/Document Health/DocumentHealth';
import EditorHeader from './Components/EditorHeader';
import EditorSection from './Components/EditorSection';
import AddBlockMenu from './Components/AddBlockMenu';
import FloatingAIBar from './Components/FloatingAIBar';
import './Editor.scss';
import Loader from 'Components/Loader/Loader';
import BriefDrawer from 'Components/Drawers/BriefDrawer/BriefDrawer';
import useBrief from 'hooks/briefs/useBrief';
import { updateTask } from 'services/contentPlannerService';
import { error as errorToast } from 'helpers/toaster';
import Header from './Components/Header';
import { generateStreamResponse } from 'helpers/openAIChat';
import ContentGenerationModal from 'Containers/Modal/ContentGenerationModal/ContentGenerationModal';
import { updateDocument } from 'services/documentService';
import Settings from './Settings';
import Publish from './Publish';
import { GENERATE_INNER_HTML_PROMPT_POSTFIX } from 'constants';
import { copyToClipboard } from 'helpers';
import {
  error as renderError,
  success as renderSuccess,
} from 'helpers/toaster.js';
import AICompanionPanel from './Components/AICompanionPanel';
import ChatHistory from 'Containers/Questionnaire/ChatHistory';
import LiveEditor from 'Containers/Questionnaire/LiveEditor';
import parse from 'html-react-parser';
import { useParams } from 'react-router-dom';
import { publishDocuments } from 'services/integrationService';
import { useSelector } from 'react-redux';
import UpgradeModal from 'Containers/Modal/UpgradeModal/UpgradeModal';
import MergePublishSetting from './MergePublishSetting.jsx/MergePublishSetting';
import PublishModal from 'Containers/Modal/PublishModal/PublishModal';
import XThreadModal from 'Containers/Modal/XThreadModal/XThreadModal';
import TweetOrThreadModal from 'Containers/Modal/XThreadModal/TweetOrThreadModal';
import PublishSettingsModal from 'Containers/Modal/PublishSettingsModal';
import { setLoader } from 'redux/slices/loader';
import { useDispatch } from 'react-redux';

let mainContainerRef;

const getWidth = ({ contentSidebarCollapse }) => {
  if (contentSidebarCollapse) {
    return 'calc(100% - 241.5px)';
  }

  return 'calc(100% - 553px)';
};

const Editor = ({ freeStyle, setIsLoading }) => {
  const {
    comments,
    setComments,
    documentCopy,
    contentSidebarCollapse,
    showComments,
    showDocHealth,
    showEditor,
    handleContentSideBarToggle,
    lastUpdated,
    collaborators,
    documentOwner,
    hoverId,
    setHoverId,
    selection,
    docHealth,
    setDocHealth,
    url,
    setUrl,
    linkModalOpen,
    handleRewrite,
    handleWriteParagraph,
    refetchContent,
    openModal,
    closeModal,
    saveLink,
    removeLink,
    handleCopyLink,
    handleExpand,
    addComment,
    resolveComment,
    deleteComment,
    editor,
    fetchComments,
    access,
    collaborator,
    docHealthLoading,
    setSelection,
  } = useCollabEditor();

  const formSubmitRef = useRef();

  const [aiTitle, setAITitle] = useState(documentCopy?.title);
  const [streamContent, setStreamContent] = useState('');
  const [contentGenerated, setContentGenerated] = useState(false);
  const [showContentGeneration, setShowContentGeneration] = useState(false);
  const [aiContent, setAiContent] = useState('');
  const [showAiTextEditor, setShowAiTextEditor] = useState(true);
  const [isAiGenerating, setIsAiGenerating] = useState(false);

  // AI Companion Panel states for chat and version tracking
  const [chatMessages, setChatMessages] = useState([]);
  const [artifactVersions, setArtifactVersions] = useState([]);
  const [currentArtifactVersion, setCurrentArtifactVersion] = useState(0);

  // Split screen mode - transforms main editor into Chat + LiveEditor
  const [splitScreenMode, setSplitScreenMode] = useState(false);
  const [currentEditablePrompt, setCurrentEditablePrompt] = useState('');

  const [showTemplateDrawer, setShowTemplateDrawer] = useState(false);
  const [showBriefDrawer, setShowBriefDrawer] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [input, setInput] = useState('');
  const [aIaction, setAiAction] = useState();
  const [documentHeaderTab, setDocumentHeaderTab] = useState('Write');
  const [publishSelectedIntegration, setPublishSelectIntegration] = useState(
    {},
  );
  const [displaySettings, setDisplaySettings] = useState(false);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const { subscriptions } = user || {};
  const { current_subscription: currentSubscription } = subscriptions ?? {};
  const abortController = useRef();
  const { documentId } = useParams() || {};

  const briefId = documentCopy?.task_associated;

  const { task, refetch: refetchTask } = useBrief(briefId);

  const documentTemplateOption = documentCopy?.template_options?.options ?? [];

  const isFullAccess = access === 'full_access';
  const isEdit = access === 'can_edit';
  const isComment = access === 'can_comment';

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [publishMessage, setPublishMessage] = useState('');
  const [showThreadOption, setShowThreadOption] = useState(false);
  const [showPublishSettingsModal, setShowPublishSettingsModal] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    editor && setIsLoading && setIsLoading();
  }, [editor]);

  useEffect(() => {
    if (isComment) {
      editor?.setEditable(false);
    }
  }, []);

  useEffect(() => {
    if (contentGenerated) {
      handleContentGenerated();
    }
  }, [contentGenerated]);

  // Track artifact versions when AI content generation completes
  // V1 = original document, V2+ = AI-generated versions
  useEffect(() => {
    if (contentGenerated && aiContent && aIaction === 'prompt') {
      const newVersion = {
        id: Date.now(),
        version: artifactVersions.length + 1,
        content: aiContent,
        timestamp: new Date(),
        isOriginal: false,
      };
      setArtifactVersions(prev => [...prev, newVersion]);
      // Switch to the newly generated version
      setCurrentArtifactVersion(artifactVersions.length);
    }
  }, [contentGenerated, aiContent, aIaction]);

  if ((!collaborators.length <= 0 && !documentOwner) || !access) {
    return (
      <Loader loaderHeading="Loading" loaderDescription="Loading the Editor" />
    );
  }

  const shouldShowContentSettings = !freeStyle && !isComment;

  // TODO: check this method
  const secondWidthMenu = getWidth({
    contentSidebarCollapse,
  });

  const handlePublishClick = () => {
    if (formSubmitRef.current) {
      formSubmitRef.current(); // Trigger form submit
    }
  };

  // Helper to build brief context string
  const buildBriefContext = () => {
    if (!task) return '';

    const briefParts = [];
    if (task.title) briefParts.push(`Brief Title: ${task.title}`);
    if (task.description) briefParts.push(`Description: ${task.description}`);
    if (task.content_type) briefParts.push(`Content Type: ${task.content_type}`);
    if (task.target_audience) briefParts.push(`Target Audience: ${task.target_audience}`);
    if (task.tone) briefParts.push(`Tone: ${task.tone}`);
    if (task.goals) briefParts.push(`Goals: ${task.goals}`);
    if (task.keywords?.length) briefParts.push(`Keywords: ${task.keywords.join(', ')}`);

    if (briefParts.length > 0) {
      return `
----CONTENT BRIEF----
${briefParts.join('\n')}
----END CONTENT BRIEF----
`;
    }
    return '';
  };

  const handlePromptAI = async (input, model) => {
    // Get the current document content from the editor
    const documentContent = editor ? editor.getHTML() : '';
    const documentTitle = documentCopy?.title || 'Untitled Document';
    const briefContext = buildBriefContext();

    // Create a comprehensive system prompt with document and brief context
    const systemPrompt = `
You are an AI writing assistant helping with a document titled "${documentTitle}". You have access to the current content of the document and the content brief to provide context for your responses.
${briefContext}
----DOCUMENT CONTENT----
${documentContent}
----END DOCUMENT CONTENT----

Please assist the user with their writing request while keeping the document's context and brief in mind. When appropriate, format your responses as HTML that can be directly inserted into the document.

Your responses should:
1. Match the style and tone specified in the brief (if provided)
2. Be formatted as valid HTML when providing content to be inserted
3. Be concise and focused on addressing the user's specific request
4. Align with the target audience and goals from the brief

${GENERATE_INNER_HTML_PROMPT_POSTFIX || ''}
`;
  
    const URL = `${process.env.REACT_APP_URL}/api/v1/assistant_completion_stream_data`;
    const BODY = JSON.stringify({
      prompt: input,
      system_prompt: systemPrompt,
      ai_model: model,
      max_token: 4000
    });
  
    try {
      abortController.current = new AbortController();
      setAiContent('');
      setContentGenerated(false);
      setAiAction('prompt');
      setIsAiGenerating(true);

      // Store the prompt and enable split screen mode
      setCurrentEditablePrompt(input);
      if (!splitScreenMode) {
        // Capture original document content as V1 before split screen opens
        if (documentContent.trim()) {
          const originalVersion = {
            id: Date.now(),
            version: 1,
            content: documentContent,
            timestamp: new Date(),
            isOriginal: true,
          };
          setArtifactVersions([originalVersion]);
          setCurrentArtifactVersion(0);
        }
        setSplitScreenMode(true);
      }

      await generateStreamResponse(
        URL,
        BODY,
        setAiContent,
        setContentGenerated,
        abortController?.current.signal,
        'ai-answer',
      );
      setIsAiGenerating(false);
    } catch (error) {
      console.error("Error in AI prompt:", error);
      setIsAiGenerating(false);
    }
  };

  // Handler for chat-based AI interactions with context
  const handleChatMessage = async (userMessage) => {
    if (!userMessage.trim() || isAiGenerating) return;

    // Add user message to chat
    const userMsg = {
      id: Date.now(),
      type: 'user',
      content: userMessage,
      timestamp: new Date(),
    };
    setChatMessages(prev => [...prev, userMsg]);

    // Get current AI content for context (either streaming or from version)
    const currentContent = artifactVersions[currentArtifactVersion]?.content || aiContent;

    // Get document context
    const documentContent = editor ? editor.getHTML() : '';
    const documentTitle = documentCopy?.title || 'Untitled Document';
    const briefContext = buildBriefContext();

    // Build contextual prompt
    let fullPrompt = userMessage;
    if (currentContent) {
      // Detect if it's a small, surgical edit
      const isSmallEdit = userMessage.length < 150 && (
        /\b(change|fix|update|replace|add|remove|make|set|bold|italic|color|font|title|heading|conclusion|intro|cta|button|link)\b/i.test(userMessage)
      );

      if (isSmallEdit) {
        fullPrompt = `Current content:\n${currentContent}\n\nTask: ${userMessage}\n\nCRITICAL INSTRUCTION: Make ONLY the specific change requested above. Keep all other content EXACTLY as-is, word-for-word. Do not improve, rephrase, rewrite, or modify anything else.`;
      } else {
        fullPrompt = `Current content:\n${currentContent}\n\nUser request: ${userMessage}\n\nPlease make the requested changes to the content above. Only output the updated content, not explanations.`;
      }
    }

    const systemPrompt = `You are an AI writing assistant helping with a document titled "${documentTitle}".
${briefContext}
Document context:
${documentContent}

Format your responses as valid HTML that can be directly inserted into the document. Be concise and focused. Align with the brief's tone, target audience, and goals if provided.
${GENERATE_INNER_HTML_PROMPT_POSTFIX || ''}`;

    const URL_ENDPOINT = `${process.env.REACT_APP_URL}/api/v1/assistant_completion_stream_data`;
    const BODY = JSON.stringify({
      prompt: fullPrompt,
      system_prompt: systemPrompt,
      ai_model: 'claude-sonnet-4-6',
      max_token: 4000
    });

    try {
      abortController.current = new AbortController();
      setAiContent('');
      setContentGenerated(false);
      setAiAction('prompt');
      setIsAiGenerating(true);

      await generateStreamResponse(
        URL_ENDPOINT,
        BODY,
        setAiContent,
        setContentGenerated,
        abortController?.current.signal,
        'ai-answer',
      );
      setIsAiGenerating(false);
    } catch (error) {
      console.error("Error in chat message:", error);
      setIsAiGenerating(false);
    }
  };

  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (newVersion >= 0 && newVersion < artifactVersions.length) {
      setCurrentArtifactVersion(newVersion);
    }
  };

  // Handler for exiting split screen mode (collapse chat)
  const handleExitSplitScreen = () => {
    setSplitScreenMode(false);
    // Switch back to Document Health view
    handleContentSideBarToggle('dochealth');
  };

  // Handler for editing the prompt
  const handleEditPrompt = () => {
    // Keep split screen open, prompt editing is handled inline
  };

  // Handler for regenerating with edited prompt
  const handleRegeneratePrompt = async (editedPromptText) => {
    if (!editedPromptText.trim() || isAiGenerating) return;

    setCurrentEditablePrompt(editedPromptText);

    const documentContent = editor ? editor.getHTML() : '';
    const documentTitle = documentCopy?.title || 'Untitled Document';
    const briefContext = buildBriefContext();

    const systemPrompt = `You are an AI writing assistant helping with a document titled "${documentTitle}".
${briefContext}
Document context:
${documentContent}

Format your responses as valid HTML that can be directly inserted into the document. Align with the brief's tone, target audience, and goals if provided.
${GENERATE_INNER_HTML_PROMPT_POSTFIX || ''}`;

    const URL_ENDPOINT = `${process.env.REACT_APP_URL}/api/v1/assistant_completion_stream_data`;
    const BODY = JSON.stringify({
      prompt: editedPromptText,
      system_prompt: systemPrompt,
      ai_model: 'claude-sonnet-4-6',
      max_token: 4000
    });

    try {
      abortController.current = new AbortController();
      setAiContent('');
      setContentGenerated(false);
      setAiAction('prompt');
      setIsAiGenerating(true);

      await generateStreamResponse(
        URL_ENDPOINT,
        BODY,
        setAiContent,
        setContentGenerated,
        abortController?.current.signal,
        'ai-answer',
      );
      setIsAiGenerating(false);
    } catch (error) {
      console.error("Error regenerating:", error);
      setIsAiGenerating(false);
    }
  };

  // Handler for saving content directly (stay in split screen)
  const handleSaveToDocument = async () => {
    const contentToSave = artifactVersions[currentArtifactVersion]?.content || aiContent;
    if (!contentToSave) return;

    try {
      await updateDocument(documentCopy.id, { content: contentToSave });
      await refetchContent(true);
      renderSuccess('Content saved to document');
    } catch (error) {
      renderError('Failed to save content');
    }
  };

  // Handler for inserting AI content into editor
  const handleReviewInEditor = () => {
    handleInsert();
    // Collapse the split screen after inserting
    setSplitScreenMode(false);
  };

  // Handler for toggling split screen mode
  const handleToggleSplitScreen = () => {
    setSplitScreenMode(prev => !prev);
  };

  // Handler for content changes in live editor
  const handleContentChange = (newContent) => {
    if (artifactVersions.length > 0) {
      setArtifactVersions(prev => prev.map((v, i) =>
        i === currentArtifactVersion ? { ...v, content: newContent } : v
      ));
    }
    setAiContent(newContent);
  };

  const handleCopy = () => {
    const blob = new Blob([aiContent], { type: 'text/html' });
    const clipboardItem = new ClipboardItem({ 'text/html': blob });
    copyToClipboard([clipboardItem], true);
    renderSuccess('Text copied to clipboard');
  };

  const updateAITitle = (aiContent) => {
    setAITitle((prevTitle) => {
      if (!aiContent) {
        return prevTitle;
      }

      const matchResult = aiContent.match(/<h1>(.*?)<\/h1>/);

      if (!matchResult || !matchResult[1]) {
        return prevTitle;
      }

      return matchResult[1];
    });
  };

  const handleInsert = () => {
    // Strip markdown code block markers if present (```html ... ```)
    const cleanContent = aiContent
      .replace(/^```html?\s*/i, '')
      .replace(/```\s*$/g, '')
      .trim();

    updateAITitle(cleanContent);

    editor.commands.insertContent(cleanContent, {
      parseOptions: {
        preserveWhitespace: false,
      },
    });
  };

  const onSubmit = async (action, values) => {
    try {
      await updateTask(briefId, values);
      await refetchTask();
    } catch (error) {
      errorToast(error);
    }
  };

  const onRegenerate = async (values) => {
    try {
      await onSubmit('edit', values);

      abortController.current = new AbortController();
      setStreamContent('');
      setContentGenerated(false);
      setShowContentGeneration(true);
      setAiAction('regenerate');

      const URL = `${process.env.REACT_APP_URL}/api/v1/completion_stream_data_style_guides`;
      const BODY = JSON.stringify({
        project_id: documentCopy.project_id,
        task_id: briefId,
      });

      await generateStreamResponse(
        URL,
        BODY,
        setStreamContent,
        setContentGenerated,
        abortController?.current.signal,
      );
    } catch (error) {
      errorToast(error);
    }
  };

  const handleContentGenerated = async () => {
    // Only update document for regenerate action, not for AI assistant prompts
    // AI assistant uses aiContent (shown in sidebar), not streamContent
    if (aIaction !== 'prompt') {
      await updateDocument(documentCopy.id, { content: streamContent });
      setShowBriefDrawer(false);
      setShowContentGeneration(false);
      await refetchContent(true);
    }
  };

  const handlePublishDoc = async (options = {}) => {
    if (currentSubscription?.plan_name === 'Basic Plan') {
      setShowUpgradeModal(true);
      return;
    }
  
    const objectKey = Object.keys(publishSelectedIntegration)[0];
    if (!publishSelectedIntegration[objectKey] || !objectKey) {
      errorToast('Please select atleast one channel to publish');
      return;
    }
  
    try {
      dispatch(
        setLoader({
          isLoading: true,
          heading: 'Processing!',
          description: 'Please wait...',
        })
      );
      const xThread = options.xThread;
      const data = await publishDocuments(documentId, {
        integration_id: publishSelectedIntegration[objectKey]?.id,
        x_thread: xThread,
      });
  
      dispatch(setLoader({ isLoading: false }));
  
      // Check if this is a LinkedIn integration response
      const isLinkedInResponse = 
        publishSelectedIntegration[objectKey]?.type === 'LinkedinIntegration' && 
        data?.data?.response && 
        data?.data?.response?.post_url;
  
      if (data?.status === 201) {
        // Pass the entire response for LinkedIn, just the message for others
        setPublishMessage(isLinkedInResponse ? data?.data : data?.data?.message);
        setIsModalOpen(true);
        setDocumentHeaderTab('write');
      } else if (data?.status === 200) {
        // Pass the entire response for LinkedIn, just the message for others
        setPublishMessage(isLinkedInResponse ? data?.data : data?.data?.message);
        setIsModalOpen(true);
      } else {
        renderError(data?.message || 'Publishing failed.');
      }
    } catch (error) {
      if (error?.response?.data?.message === 'message-too-long') {
        setShowThreadOption(true);
      } else {
        renderError(error?.response?.data?.message || 'Publishing failed.');
      }
      dispatch(setLoader({ isLoading: false }));
      console.log('Error:', error);
    }
  };

  const checkPublishDoc = () => {
    try {
      if (currentSubscription?.plan_name === 'Basic Plan') {
        setShowUpgradeModal(true);
        return false;
      }

      const objectKey = Object.keys(publishSelectedIntegration)[0];
      if (!publishSelectedIntegration[objectKey] || !objectKey) {
        errorToast('Please select atleast one channel to publish');
        return false;
      }
      return true;
    } catch (error) {
      console.log('Error:', error);
    }
  };

  const checkIntegrations = (integrations) => {
    // Check if the object is empty
    if (Object.keys(integrations).length === 0) {
      return false;
    }

    // Check if all integrations have is_active set to false
    const allInactive = Object.values(integrations).every(
      (integration) => integration.is_active === false,
    );

    return !allInactive; // If all are inactive, it returns false
  };

  const documentHeadersComponents = {
    Write: (
      <EditorSection
        editor={editor}
        setAITitle={setAITitle}
        handleRewrite={handleRewrite}
        comments={comments}
        setComments={setComments}
        fetchComments={fetchComments}
        isComment={isComment}
        hoverId={hoverId}
        selection={selection}
        secondWidth={secondWidthMenu}
        handleWriteParagraph={handleWriteParagraph}
        openModalLink={openModal}
        mainContainerRef={mainContainerRef}
        handleExpandChild={handleExpand}
        addComment={addComment}
        resolveComment={resolveComment}
        deleteComment={deleteComment}
        setSelection={setSelection}
      />
    ),
    // Settings: <Settings />,
    Publish: (
      <MergePublishSetting
        setPublishSelectIntegration={setPublishSelectIntegration}
        publishSelectedIntegration={publishSelectedIntegration}
        displaySettings={displaySettings}
        setDocumentHeaderTab={setDocumentHeaderTab}
        setDisplaySettings={setDisplaySettings}
        checkIntegrations={checkIntegrations}
        handlePublishDoc={handlePublishDoc}
        formSubmitRef={formSubmitRef}
        showUpgradeModal={showUpgradeModal}
        setShowUpgradeModal={setShowUpgradeModal}
      />
    ),
    // Publish: !checkIntegrations(publishSelectedIntegration)  ? (
    //   <Publish
    //     setPublishSelectIntegration={setPublishSelectIntegration}
    //     publishSelectedIntegration={publishSelectedIntegration}
    //     displaySettings={displaySettings}
    //     setDisplaySettings={setDisplaySettings}
    //     checkIntegrations={checkIntegrations}
    //   />
    // ) : (
    //   <Settings
    //     setDisplaySettings={setDisplaySettings}
    //     // other props for Settings if needed
    //   />
    // ),
  };

  return (
    <span
      id="suggestionPortal"
      ref={(el) => {
        mainContainerRef = el;
      }}
    >
      <BubbleMenu
        pluginKey="bubbleMenuLink"
        className="bubble-menu-light"
        tippyOptions={{ duration: 150, placement: 'auto' }}
        editor={editor}
        shouldShow={({ editor, from, to }) => {
          // only show the bubble menu for links.
          return from === to && editor.isActive('link');
        }}
      >
        <button className="button" onClick={openModal}>
          <BiPencil size={20} />
        </button>
        <button className="button-remove" onClick={removeLink}>
          <BiUnlink size={20} />
        </button>
        <button className="button-remove" onClick={handleCopyLink}>
          <BiCopy size={20} />
        </button>
      </BubbleMenu>
      {!isComment && (
        <FloatingMenu
          editor={editor}
          tippyOptions={{
            duration: 100,
            maxWidth: '100%',
            border: '2px solid red',
          }}
        >
          <AddBlockMenu
            editor={editor}
            inline={true}
            freeStyle={freeStyle}
            handlePromptAI={handlePromptAI}
            aiContent={aiContent}
            handleInsert={handleInsert}
            handleCopy={handleCopy}
            showDrawer={showDrawer}
            setShowDrawer={setShowDrawer}
            setShowAiTextEditor={setShowAiTextEditor}
            showEditor={() => handleContentSideBarToggle('editor')}
            input={input}
            setInput={setInput}
          />
        </FloatingMenu>
      )}
      {linkModalOpen && (
        <LinkModal
          url={url}
          isOpen={linkModalOpen}
          onRequestClose={closeModal}
          contentLabel="Edit Link Modal"
          closeModal={closeModal}
          onChangeUrl={(e) => setUrl(e.target.value)}
          onSaveLink={saveLink}
          onRemoveLink={removeLink}
          setUrl={setUrl}
        />
      )}
      <Header
        document={documentCopy}
        contentSidebarCollapse={contentSidebarCollapse}
        refetchContent={refetchContent}
        setShowComments={handleContentSideBarToggle}
        lastUpdated={lastUpdated}
        showComments={showComments}
        shouldShowContentSettings={shouldShowContentSettings}
        isEdit={isEdit}
        isComment={isComment}
        comments={comments}
        canDelete={isFullAccess}
        collaborator={collaborator}
        collaborators={collaborators}
        setShowTemplateDrawer={setShowTemplateDrawer}
        briefId={briefId}
        setShowBriefDrawer={setShowBriefDrawer}
        freeStyle={freeStyle}
        fromEditorDoc={true}
        handlePublishDoc={handlePublishDoc}
        documentHeaderTab={documentHeaderTab}
        setDocumentHeaderTab={setDocumentHeaderTab}
        onPublish={handlePublishClick}
        checkPublishDoc={checkPublishDoc}
        onOpenPublishSettings={() => setShowPublishSettingsModal(true)}
      />

      <div className={`editor-container d-flex ${splitScreenMode ? 'split-screen-mode' : ''}`}>
        <div
          className={`second ${contentSidebarCollapse ? 'full-width' : 'width_editor_text_area'} ${splitScreenMode ? 'editor-split-active' : ''}`}
        >
          <EditorHeader
            document={documentCopy}
            docHealth={docHealth}
            aiTitle={aiTitle}
            lastUpdated={lastUpdated}
            showDocHealth={() => handleContentSideBarToggle('dochealth')}
            shouldShowContentSettings={shouldShowContentSettings}
            isComment={isComment}
            setShowTemplateDrawer={setShowTemplateDrawer}
            briefId={briefId}
            setShowBriefDrawer={setShowBriefDrawer}
            contentSidebarCollapse={contentSidebarCollapse}
            documentHeaderTab={documentHeaderTab}
            setDocumentHeaderTab={setDocumentHeaderTab}
            contentType={task?.content_type}
            splitScreenMode={splitScreenMode}
            onExitSplitScreen={handleExitSplitScreen}
          />

          {/* Split Screen Mode: Chat (left) + Live Document (right) */}
          {splitScreenMode ? (
            <div className="split-screen-container">
              <div className="split-chat-panel">
                <ChatHistory
                  messages={chatMessages}
                  isGenerating={isAiGenerating}
                  onSendMessage={handleChatMessage}
                  initialPrompt={currentEditablePrompt}
                  onRegeneratePrompt={handleRegeneratePrompt}
                  currentActivity={isAiGenerating ? { id: 'generating', content: 'AI is generating...' } : null}
                />
              </div>
              <div className="split-editor-panel">
                <LiveEditor
                  streamContent={isAiGenerating ? aiContent : (artifactVersions[currentArtifactVersion]?.content || aiContent)}
                  isGenerating={isAiGenerating}
                  contentGenerated={contentGenerated}
                  onContentChange={handleContentChange}
                  currentVersion={currentArtifactVersion + 1}
                  totalVersions={artifactVersions.length}
                  onVersionChange={(v) => handleVersionChange(v - 1)}
                  onReviewInEditor={handleReviewInEditor}
                  onSaveToDocument={handleSaveToDocument}
                />
              </div>
            </div>
          ) : (
            <>
              {documentHeadersComponents[documentHeaderTab] ||
                documentHeadersComponents.Write}
            </>
          )}

          {!isComment && !splitScreenMode && (
            <FloatingAIBar
              handlePromptAI={handlePromptAI}
              input={input}
              setInput={setInput}
              disabled={isComment}
              showEditor={() => handleContentSideBarToggle('editor')}
            />
          )}
        </div>
        <div
          className={`third ${contentSidebarCollapse ? 'container-closed' : ''} ${splitScreenMode ? 'split-screen-sidebar' : ''}`}
        >
          {showComments && (
            <CommentsSidebar
              comments={comments}
              fetchComments={fetchComments}
              setHoverId={setHoverId}
              hoverId={hoverId}
              resolveComment={resolveComment}
              deleteComment={deleteComment}
            />
          )}
          {showDocHealth && (
            <DocumentHealth
              editor={editor}
              docHealth={docHealth}
              setDocHealth={setDocHealth}
              docHealthLoading={docHealthLoading}
              onClose={() => handleContentSideBarToggle('close')}
            />
          )}
          {showEditor && !splitScreenMode && (
            <AICompanionPanel
              aiContent={aiContent}
              handleInsert={handleInsert}
              handleCopy={handleCopy}
              onClose={() => handleContentSideBarToggle('close')}
              isGenerating={isAiGenerating}
              contentGenerated={contentGenerated}
              onSendMessage={handleChatMessage}
              chatMessages={chatMessages}
              artifactVersions={artifactVersions}
              currentArtifactVersion={currentArtifactVersion}
              onVersionChange={handleVersionChange}
              showPanel={showEditor}
              isExpanded={splitScreenMode}
              onToggleExpand={handleToggleSplitScreen}
              onSaveToDocument={handleSaveToDocument}
              onReviewInEditor={handleReviewInEditor}
              currentEditablePrompt={currentEditablePrompt}
              onRegeneratePrompt={handleRegeneratePrompt}
            />
          )}
        </div>
      </div>

      {shouldShowContentSettings && (
        <ContentSettings
          show={showTemplateDrawer}
          onHide={() => setShowTemplateDrawer(false)}
          documentTemplateOptions={documentTemplateOption}
          refetchContent={refetchContent}
        />
      )}
      {showBriefDrawer && (
        <BriefDrawer
          show={showBriefDrawer}
          onHide={() => setShowBriefDrawer(false)}
          task={task}
          onSubmit={onSubmit}
          regenerate
          regenerateFn={onRegenerate}
          action="edit"
        />
      )}

      {showContentGeneration && (
        <ContentGenerationModal
          onHide={() => {
            setShowContentGeneration(false);
            setStreamContent('');
            setContentGenerated(false);
            abortController?.current.abort();
          }}
          show={showContentGeneration}
          string={streamContent}
          contentGenerated={contentGenerated}
        />
      )}

      {showUpgradeModal && (
        <UpgradeModal
          show={showUpgradeModal}
          onHide={() => setShowUpgradeModal(false)}
        />
      )}
      {isModalOpen && (
        <PublishModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          message={publishMessage}
        />
      )}
      {showThreadOption && (
        <XThreadModal
          isOpen={showThreadOption}
          convertItToThread={() => handlePublishDoc({ xThread: true })}
          onClose={() => setShowThreadOption(false)}
        />
      )}
      {showPublishSettingsModal && (
        <PublishSettingsModal
          isOpen={showPublishSettingsModal}
          onClose={() => setShowPublishSettingsModal(false)}
          document={documentCopy}
          refetchContent={refetchContent}
          onPublishSuccess={(data) => {
            setShowPublishSettingsModal(false);
            setPublishMessage(data?.message || 'Content published successfully!');
            setIsModalOpen(true);
          }}
        />
      )}
    </span>
  );
};

export default Editor;